/**
 * @fileoverview Sales / Banking tab strip of the Capabilities screen.
 * @description An underlined tab strip choosing which app's roles are shown. The Banking tab is
 * locked while there are unsaved edits, so a half-finished change can't be left behind by
 * switching away; its tooltip says to save or cancel first.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import type { CapabilitiesTab } from '@/features/capabilities/types/capabilities.types'

interface CapabilitiesTabsProps {
  value: CapabilitiesTab
  onChange: (tab: CapabilitiesTab) => void
  /** Locks the tab that isn't showing, while edits are in progress. */
  locked: boolean
}

const TABS: CapabilitiesTab[] = ['sales', 'banking']

/**
 * @component
 * @param {CapabilitiesTabsProps} props - The selected tab, its change handler and the lock.
 * @param {CapabilitiesTab} props.value - The tab showing now.
 * @param {(tab: CapabilitiesTab) => void} props.onChange - Called with the tab the viewer picks.
 * @param {boolean} props.locked - True while there are unsaved edits; disables the other tab.
 */
function CapabilitiesTabs({ value, onChange, locked }: CapabilitiesTabsProps) {
  const { t } = useTranslation()

  return (
    <div role="tablist" className="flex gap-1 border-b border-rule" data-testid="capabilities-tabs">
      {TABS.map((tab) => {
        const selected = tab === value
        const disabled = locked && !selected

        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            title={disabled ? t('capabilities.tabs.locked') : undefined}
            onClick={() => onChange(tab)}
            data-testid={`capabilities-tab-${tab}`}
            className={`-mb-px cursor-pointer border-b-2 px-[18px] py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
              selected
                ? 'border-primary-dark text-primary-dark'
                : 'border-transparent text-slate hover:text-ink'
            }`}
          >
            {t(`capabilities.tabs.${tab}`)}
          </button>
        )
      })}
    </div>
  )
}

export default CapabilitiesTabs
